/**
 * /finance/expenses — every expense the caller may see (RLS decides: finance sees all; others see their own,
 * their events' and those they approve). Finance requesters can also add an expense with no event.
 */
import { useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../../components/common/Button";
import ExpenseTable from "../../components/finance/ExpenseTable";
import ExpenseFormModal from "../../components/finance/ExpenseFormModal";
import ExpenseDetailModal from "../../components/finance/ExpenseDetailModal";
import { supabase } from "../../services/supabase";
import {
  financeErrorMessage,
  getFinanceLookups,
  listExpenses,
  STATUS_LABEL,
} from "../../services/finance.service";
import type { FinanceAccess } from "../../types/finance.types";

async function eventTitles(ids: string[]): Promise<Map<string, string>> {
  if (ids.length === 0) {
    return new Map();
  }
  const { data } = await supabase
    .from("events")
    .select("id, title")
    .in("id", ids);
  return new Map(
    (data ?? []).map((e: { id: string; title: string }) => [e.id, e.title]),
  );
}

export default function FinanceExpenses() {
  const access = useOutletContext<FinanceAccess>();
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const q = useQuery({
    queryKey: ["finance-expenses", "all", status, search, unlinkedOnly],
    queryFn: () =>
      listExpenses({ status: status || undefined, search, unlinkedOnly }),
  });
  const lookupsQ = useQuery({
    queryKey: ["finance-lookups"],
    queryFn: getFinanceLookups,
    staleTime: 60_000,
  });
  const ids = useMemo(
    () =>
      Array.from(
        new Set(
          (q.data ?? []).map((x) => x.event_id).filter((v): v is string => !!v),
        ),
      ),
    [q.data],
  );
  const titlesQ = useQuery({
    queryKey: ["finance-event-titles", ids],
    queryFn: () => eventTitles(ids),
  });

  return (
    <div className="space-y-4" data-testid="finance-expenses-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label
              htmlFor="fx-filter-status"
              className="block text-xs font-medium text-gray-600"
            >
              Status
            </label>
            <select
              id="fx-filter-status"
              className="mt-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="fx-filter-search"
              className="block text-xs font-medium text-gray-600"
            >
              Search
            </label>
            <input
              id="fx-filter-search"
              placeholder="Expense number or description"
              className="mt-1 w-64 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 pb-1.5 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={unlinkedOnly}
              onChange={(e) => setUnlinkedOnly(e.target.checked)}
            />
            Only expenses without an event
          </label>
        </div>
        {access.can_create_unlinked && (
          <Button onClick={() => setAddOpen(true)}>
            Add expense without an event
          </Button>
        )}
      </div>
      <p className="text-sm text-gray-500">
        Expenses for an event are added from the event&apos;s Budget &amp;
        Expenses tab.
        {access.is_finance_admin
          ? " As finance you see every expense."
          : // 1140: worded to hold for admins too (with admins_have_event_expense_access on they have every event)
            " You see expenses you created or approve, and those on every event you have finance access to."}
      </p>
      <div className="rounded-lg border border-gray-200 bg-white">
        {q.isLoading ? (
          <p className="py-8 text-center text-sm text-gray-500">Loading…</p>
        ) : q.error ? (
          <p className="py-8 text-center text-sm text-red-700">
            {financeErrorMessage(q.error)}
          </p>
        ) : (
          <ExpenseTable
            expenses={q.data ?? []}
            lookups={lookupsQ.data}
            eventTitles={titlesQ.data}
            showEvent
            onOpen={setOpenId}
            empty="No expenses match."
          />
        )}
      </div>
      <ExpenseFormModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={(e) => {
          setAddOpen(false);
          qc.invalidateQueries({ queryKey: ["finance-expenses"] });
          setOpenId(e.id);
        }}
        eventId={null}
        isFinanceAdmin={!!access.is_finance_admin}
      />
      <ExpenseDetailModal expenseId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
